import { IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import {
  Button,
  CreateButton,
  EditButton,
  ExportButton,
  Icons,
  List,
  notification,
  Popconfirm,
  Space,
  Table,
} from '@pankod/refine-antd';
import { useCallback, useEffect, useState } from 'react';
import { ICategory } from 'interfaces';
import { SearchNew } from 'components';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import styles from './style.module.css';
import { deleteExpenseCategory, getAllExpenseCategory } from 'services/expense-category';
import { SearchIndex } from 'services/meilisearch';

interface IExpense extends ICategory {}

export const ExpenseCategoryList: React.FC<IResourceComponentsProps> = () => {
  const [categories, setCategories] = useState<IExpense[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const { push, goBack } = useNavigation();

  const fetchDataOnMount = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllExpenseCategory({ searchString: searchText });
      const categoriesData = data.data;
      setCategories(categoriesData);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [searchText]);

  useEffect(() => {
    fetchDataOnMount();
  }, [fetchDataOnMount]);

  const onSelectOption = async (searchText: string) => {
    setSearchText(searchText);
  };

  const deleteConfirmHandler = async (id: string) => {
    try {
      await deleteExpenseCategory(id);
      const data = await getAllExpenseCategory();
      const categoriesData = data.data;
      setCategories(categoriesData);
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a expense category',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete expense category',
      });
    }
  };

  const onExportButtonClick = async () => {
    try {
      const data = await getAllExpenseCategory();
      const categoriesData = data.data;
      const rows = [['Name', 'Description']];
      categoriesData.forEach((element) => {
        rows.push([element.name, element.description]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
  };

  const pageHeaders = (
    <Space>
      <CreateButton
        type="primary"
        onClick={() => push('/expenses/expense-categories/create')}
        accessControl={{ enabled: false }}
      />
      <ExportButton onClick={onExportButtonClick} />
    </Space>
  );

  return (
    <List
      headerProps={{ extra: pageHeaders }}
      pageHeaderProps={{ onBack: goBack }}
      title="Expense Categories"
    >
      <MetaData />
      <SearchNew
        placeholder="Search by Name"
        size="large"
        className={styles.search}
        index={SearchIndex.ExpenseCategory}
        groups={['name']}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMount}
      />
      <Table dataSource={categories} loading={isLoading} rowKey="id" scroll={{ x: true }}>
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column dataIndex="description" title="Description" />
        <Table.Column<IExpense>
          title="Actions"
          dataIndex="actions"
          render={(_text, record): React.ReactNode => {
            const endpoint = `edit/${record.id}`;
            return (
              <Space>
                <EditButton
                  size="small"
                  recordItemId={record.id}
                  hideText
                  ignoreAccessControlProvider
                  onClick={() => push(endpoint)}
                />
                <Popconfirm
                  title="Are you sure?"
                  okText="Delete"
                  onConfirm={async () => {
                    await deleteConfirmHandler(record.id);
                  }}
                >
                  <Button size="small" danger icon={<Icons.DeleteOutlined />} />
                </Popconfirm>
              </Space>
            );
          }}
        />
      </Table>
    </List>
  );
};
